<template>
    <div
        v-if="store.state.showingModal === 'library-modal'"
        class="library-modal"
    >
        <div
            class="cancel-button"
            @click="close"
        >
            <Icon
                icon="cancel"
            />
        </div>
        <div v-show="!showCollections">
            <div class="action-area">
                <span v-if="loggedIn">
                    {{t('show','Show')}}
                    <select v-model="selectedCollection" @change="collectionChange">
                        <option v-for="(item, i) in collectionOptions" :value="item.value || item.title">{{ item.title }}</option>
                    </select>
                </span>
                <input
                    v-model="searchStr"
                    @focus="enableTyping"
                    @input="checkHex"
                    @change="search"
                    maxlength="60"
                />
                <Icon
                    @click="search"
                    class="muted nudge-down"
                    icon="search"
                    :title="t('search the bead library','Search the bead library')"
                />
            </div>
            <div class="options-area" v-if="selectedCollection === 'All'">
                {{t('color match','Color Match')}}
                <input type="checkbox" v-model="colorMatch">
                <button v-if="loggedIn" @click="addBeadToLibrary" class="micro-button">
                    {{ t('new bead', 'New Bead') }}
                </button>
            </div>
        </div>

        <div class="library-table-holder">
            <table class="library-table">
                <tr
                    v-for="(item, i) in store.state.beadsToShow.slice(this.startIndex, this.endIndex)" :key="i"
                    class="item-row"
                >
                    <td @click="clickOn(item)">
                        <img :src="getImg(item)">
                    </td>
                    <td @click="clickOn(item)" class="item-meta">
                        <div class="item-title">
                            <span class="item-id">{{ item.title }}</span>&nbsp;
                            <span v-if="item.stock !== null">
                                <span class="tube-box">
                                    <span class="stock-button" :style="stockStyle(1, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(2, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(3, item.stock)"></span>
                                    <span class="stock-button" :style="stockStyle(4, item.stock)"></span>
                                </span>
                                <span class="tube-lid"></span>
                                <span class="tube-handle"></span>
                            </span>
                        </div>
                        <div class="item-details">
                            {{ [item.details, item.description].filter(e => e).join(' | ') }}
                        </div>
                    </td>
                    <td>
                        <Icon
                            @click="add(item)"
                            class="muted"
                            icon="add"
                            :title="t('bead library add style to palette', 'Add style to palette')"
                        />
                    </td>
                    <td>
                        <Icon
                            @click="swap(item)"
                            class="muted"
                            icon="swap_horiz"
                            :title="t('bead library swap in selected style', 'Swap in selected style')"
                        />
                    </td>
                    <td v-if="loggedIn">
                        <Icon
                            @click="bookmark(item)"
                            class="muted"
                            :icon="isBookmarked(item) ? 'bookmark' : 'bookmark_border'"
                            :style="getBookmarkStyle(item)"
                            :title="t('add to collection', 'Add to collection')"
                        />
                    </td>
                </tr>
            </table>
        </div>
        <div class="pagination-area centered" v-if="pageArr.length > 1">
            <button @click="previous" :disabled="onFirstPage">&lt;</button>
            <button
                @click="goToPage(num)"
                v-for="(num, i) in pageArr" :key="i"
                :class="currentResultsPage === num - 1 ? 'highlighted' : ''"
            >
                {{ num }}
            </button>
            <button @click="next" :disabled="onLastPage">&gt;</button>
        </div>
        <div class="collections-area" v-show="showCollections">
            <div v-if="editingCollection">
                <div class="subtitle">{{t('edit collection','Edit Collection')}}</div>
                    <div class="add-collection-area">
                        <span class="form-element">{{t('title', 'Title')}}</span>
                        <input class="form-element" type="text" v-model="editCollectionName">
                        <span class="form-element" >{{t('color', 'Color')}}</span>
                        <input class="form-element" type="color" v-model="editCollectionColor">
                    </div>
                    <div class="table-button-area">
                        <button @click="completeCollectionEdit">{{t('done','Done')}}</button>
                        <button class="warning-button" @click="deleteCollection">{{t('delete','Delete')}}</button>
                    </div>
            </div>
            <div v-else>
                <div class="subtitle">{{t('add to collection','Add to collection')}}</div>
                <table class="collection-table">
                    <tr class="collection-entry" v-for="(c, i) in store.state.collections" :key="i">
                        <td @click="toggleBeadInCollection(c)" class="bookmark-col"><Icon :icon="beadInCollection(c) ? 'bookmark' : 'bookmark_border'" :style="`color: ${c.color};`"/></td>
                        <td @click="toggleBeadInCollection(c)">{{ c.title }}</td>
                        <td @click="editCollection(c)" class="bookmark-col"><Icon icon="edit"/></td>
                    </tr>
                    <tr v-if="!store.state.collections.length">
                        <td>({{t('you have no collections yet','You have no collections yet')}})</td>
                    </tr>
                </table>
                <div>
                    <div class="new-collection-form">
                        <div class="subtitle">{{t('new collection','New Collection')}}</div>
                        <div class="add-collection-area">
                            <span class="form-element">{{t('title','Title')}}</span>
                            <input class="form-element" type="text" v-model="newCollectionName">
                            <span class="form-element" >{{t('color', 'Color')}}</span>
                            <input class="form-element" type="color" v-model="newCollectionColor">
                            <button class="mini-button form-add-button" @click="addCollection">
                                <Icon class="nudge-down-2" icon="add"/>
                            </button>
                        </div>
                    </div>
                    <button @click="cancelCollectionEdit">{{t('done','Done')}}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { utils } from '../myapp/src/utils';
import { beadLibrary } from '../myapp/src/tools/beadLibrary';
import Icon from '../components/Icon.vue';
import { palette } from '../myapp/src/palette';
import { myInput } from '../myapp/src/input';
import { modes } from '../myapp/src/modes';
import { settings } from '../myapp/src/settings';

export default {
    data() {
        return {
            loggedIn,
            searchStr: '',
            colorMatch: false,
            currentResultsPage: 0,
            resultsPerPage: 15,
            pagesToShow: 7, // must be odd number
            showCollections: false,
            newCollectionName: '',
            newCollectionColor: '',
            editCollectionName: '',
            editCollectionColor: '',
            selectedCollection: 'All',
            editingCollection: false,
        }
    },
    components: {
        Icon
    },
    watch: {
        colorMatch: function () {
            b3.store.mySet('colorMatch', this.colorMatch);
            this.colorMatch ?
                beadLibrary.displayBeads({method: 'color'}):
                beadLibrary.displayBeads({method: 'filter'});
        }
    },
    methods: {
        collectionChange() {
            let config;
            if (this.selectedCollection === 'All') {
                config = {method: 'filter'};
            } else if (this.selectedCollection === 'All in stock') {
                config = {method: 'stock'};
            } else {
                const collection = this.store.state.collections.find(e => e.title === this.selectedCollection);
                config = {method: 'stash', collection};
            }
            this.currentResultsPage = 0;
            this.searchStr = '';
            beadLibrary.displayBeads(config);
        },
        enableTyping() {
            myInput.enableTyping();
        },
        addBeadToLibrary() {
            const prefix = this.store.state.libraryShape === 'cylinder' ? 'cylinder_' : '';

            const existingIds = this.store.state.customBeads
                .filter(b => b.id.slice(0, 3) === 'ZZZ')
                .map(b => Number(b.id.slice(3)))
                .concat(0);

            const nextId = Math.max(...existingIds) + 1;
            const id = `ZZZ${nextId}`;

            const swatch = {
                id,
                title: '',
                details: '',
                description: '',
                stock: 0,
                fillColor: 4243391,
                finishName: this.store.state.plasticMode ?
                    'plastic' : `${prefix}matte`,
                inner: 'opaque',
                secondColor: null,
                isNew: true,
                isLibraryBead: true,
                hasBeadsOnCanvas: () => false
            };
            b3.ee.emit('editSwatch', swatch);
            b3.store.mySet('showingModal', 'swatch-edit');

        },
        addCollection() {
            const element = {
                title: this.newCollectionName,
                color: this.newCollectionColor || '#000000',
                beadIdList: []
            };
            const c = this.store.state.collections.concat(element);
            this.updateCollections(c);
            this.newCollectionName = '';
            this.newCollectionColor = '';
        },
        beadInCollection(c) {
            const id = this.store.state.beadsToShow[0]?.id;
            if (!id) return false;
            return c.beadIdList.includes(id);
        },
        toggleBeadInCollection(c) {
            const id = this.store.state.beadsToShow[0]?.id;
            if (!id) return false;
            c.beadIdList = c.beadIdList.includes(id) ?
                c.beadIdList.filter(e => e !== id):
                c.beadIdList.concat(id);

            c.beadIdList = c.beadIdList.filter(e => typeof e === 'string');

            this.updateCollections(this.store.state.collections);
        },
        editCollection(c) {
            this.collectionToEdit = c;
            this.editCollectionName = c.title;
            this.editCollectionColor = c.color;
            this.editingCollection = true;
        },
        completeCollectionEdit() {
            this.collectionToEdit.title = this.editCollectionName;
            this.collectionToEdit.color = this.editCollectionColor;
            this.editingCollection = false;
        },
        deleteCollection() {
            const c = this.store.state.collections.filter(e => e !== this.collectionToEdit);
            this.updateCollections(c);
            this.editingCollection = false;
        },
        updateCollections(c) {
            b3.store.mySet('collections', c);
            settings.preferences.collections = c;
            settings.update();
        },
        cancelCollectionEdit() {
            this.selectedCollection = 'All';
            beadLibrary.displayBeads({method: 'filter'});
            this.showCollections = false;
        },
        reset() {
            this.currentResultsPage = 0;
            this.showCollections = false;
            this.selectedCollection = 'All';
        },
        close() {
            this.showCollections = false;
            this.editingCollection = false;
            modes.start('draw');
        },
        clickOn(item) {
            if (!this.loggedIn) return;
            const swatch = {
                ...item,
                isLibraryBead: true,
                hasBeadsOnCanvas: () => false
            };
            b3.ee.emit('editSwatch', swatch);
            b3.store.mySet('showingModal', 'swatch-edit');
        },
        bookmark(item) {
            b3.store.mySet('beadsToShow', [item]);
            this.newCollectionName = '';
            this.newCollectionColor = '';
            this.showCollections = true;
        },
        add(item) {
            const description = `${item.details} (${item.title})`;
            beadLibrary.addSwatchFromLibrary({...item, description});
        },
        swap(item) {
            const description = `${item.details} (${item.title})`;
            beadLibrary.swapIn({...item, description});
        },
        search() {
            this.selectedCollection = 'All';
            this.currentResultsPage = 0;
            beadLibrary.displayBeads({method: 'filter', str: this.searchStr});
        },
        checkHex() {
            const str = this.searchStr;
            if (str.length !== 6) return;
            if (/^[a-f0-9]+$/.test(str)) palette.setHex(str);
        },
        getImg(item) {
            return utils.getImg(item);
        },
        previous() {
            if (this.onFirstPage) return;
            this.currentResultsPage--;
        },
        next() {
            if (this.onLastPage) return;
            this.currentResultsPage++;
        },
        goToPage(num) {
            if (this.currentResultsPage === num - 1) return;
            this.currentResultsPage = num - 1;
        },
        isBookmarked(item) {
            return this.store.state.collections
                .map(c => c.beadIdList)
                .flat()
                .includes(item.id);
        },
        getBookmarkStyle(item) {
            const collection = this.store.state.collections
                .find(c => c.beadIdList.includes(item.id));
            if (!collection) return ''
            return `color: ${collection.color};`
        },
        stockStyle(n, stock) {
            const colors = ['black', 'red', 'chocolate', '#cccc00', '#33dd33'];
            const color = n > stock ?
                'grey': colors[stock];

            return `background-color: ${color};`;
        }
    },
    computed: {
        pageArr: function () {
            const size = Math.min(this.numOfPages, this.pagesToShow);
            const startingIndex = this.currentResultsPage < (this.pagesToShow + 1) / 2 ?
                1 :
                this.currentResultsPage < this.numOfPages - ((this.pagesToShow - 1) / 2) ?
                    this.currentResultsPage - ((this.pagesToShow - 1) / 2) + 1 :
                    Math.max(1, this.numOfPages - (this.pagesToShow - 1));

            return new Array(size).fill('').map((e, i) => i + startingIndex);
        },
        numOfPages: function () {
            return Math.ceil(this.store.state.beadsToShow.length / this.resultsPerPage);
        },
        onFirstPage: function () {
            return this.currentResultsPage === 0;
        },
        onLastPage: function () {
            return this.currentResultsPage >= this.numOfPages - 1;
        },
        startIndex: function () {
            return this.currentResultsPage * this.resultsPerPage;
        },
        endIndex: function () {
            return (this.currentResultsPage + 1) * this.resultsPerPage;
        },
        collectionOptions: function () {
            return [
                {title: this.t('all', 'All'), value: 'All'},
                {title: this.t('all in stock', 'All in stock'), value: 'All in stock'}
            ].concat(this.store.state.collections)
        },
    },
    mounted() {
        this.ee.on('resetBeadLibrary', () => {
            this.reset();
        });
        this.ee.on('beadLibraryQuery', obj => {
            if (!this.colorMatch && obj.trigger === 'swatch') return;
            this.reset();
            beadLibrary.displayBeads({method: 'color', color: obj.color});
        })
    }
}
</script>

<style lang="scss">
@import '../assets/colors.scss';
.library-modal {
    z-index: 1;
    position: absolute;
    box-shadow: 0 0 5px black;
    background-color: $off_white;
    border-radius: 5px;
    padding: 20px;
    top: 75px;
    left: 96px;
    width: 410px;
    .centered {
        display: flex;
        justify-content: center;
    }
    .cancel-button {
        position: absolute;
        right: 7px;
        top: 5px;
        height: 23px;
        &:hover {
            color: $warning;
            cursor: pointer;
        }
        span {
            position: relative;
            right: -5px;
            top: -3px;
        }
    }
    .muted {
        color: $dark_grey;
        &:hover {
            cursor: pointer;
            color: $b_theme_hover;
        }
    }
    .nudge-down {
        position: relative;
        top: 5px;
        left: 5px;
    }
    .library-table-holder {
        max-height: calc(100vh - 300px);
        overflow-y: auto;
    }
    .library-table {
        border-collapse: collapse;
        .item-row {
            &:hover {
                background-color: white;
                cursor: pointer;
            }
            .item-meta {
                padding: 5px;
                width: 280px;
            }
            .item-id {
                font-weight: bold;
            }
            .item-title {
                font-size: 12px;
            }
            .item-details {
                font-size: 12px;
            }
        }
        .stock-button {
            display: inline-block;
            width: 10px;
            height: 10px;
            margin-right: 1px;
        }
        .tube-box {
            display: inline-block;
            border: 1px solid black;
            padding: 2px 1px 2px 2px;
        }
        .tube-lid {
            display: inline-block;
            height: 19px;
            width: 12px;
            background-color: black;
            position: relative;
            top: 5px;
        }
        .tube-handle {
            position: relative;
            top: 2px;
            left: -6px;
            display: inline-block;
            height: 10px;
            width: 8px;
            border-radius: 100%;
            border: 2px solid black;
        }
    }
    .action-area {
        position: relative;
        top: -10px;
        font-size: 14px;
        select {
            width: 120px;
            margin: 0 5px;
        }
    }
    .options-area {
        font-size: 14px;
    }
    .new-collection-form {
        background-color: white;
        border-radius: 5px;
        padding: 5px 10px;
        margin: 10px 0;
    }
    .add-collection-area {
        position: relative;
        top: -5px;
        .form-element {
            display: inline-block;
            margin-right: 5px;
        }
        input {
            width: 100px;
        }
        input[type=color] {
            padding: 0;
            height: 22px;
            position: relative;
            top: 4px;
        }
        .form-add-button {
            position: relative;
            top: 4px;
        }
    }
    .collections-area {
        font-size: 14px;
        .subtitle {
            font-weight: bold;
            margin: 5px 0;
        }
        .collection-table {
            width: 100%;
            border-collapse: collapse;
        }
        .collection-entry {
            &:hover {
                cursor: pointer;
                background-color: white;
            }
        }
        .bookmark-col {
            width: 50px;
        }
    }
    .micro-button {
        display: inline-block;
        margin: 0 5px;
        padding: 1px 5px;
        position: relative;
        left: 200px;
        span {
            font-size: 16px;
        }
    }
}
</style>